/**
 * F17 T17.1.5 Commit 7 — listing definicji procesów akceptacji.
 *
 * Zastępuje placeholder `ProcessesPage.tsx`. Renderuje:
 *  - Header z buttonem "Nowy proces" (otwiera CreateProcessModal).
 *  - Pasek nad tabelą (wewnątrz jej karty, Figma „Table toolbar"): chipy statusu
 *    (Wszystkie/Szkice/Opublikowane), search (debounced 300ms), „Pokaż archiwalne".
 *  - Tabelę (ConfigurableTable): # | Nazwa | Kartoteka | Status badge | Wersja | Akcje.
 *  - Akcje wiersza: Edytuj (link do buildera) / Publikuj (POST /publish) /
 *    Archiwizuj (DELETE soft) z ConfirmDialog.
 *
 * Filtry per kolumna (Kreator Listy, popup w nagłówku) — server-side przez
 * `?headerFilters=` (mirror `KsefInvoices.tsx`/`apps/api/app/api/ksef/invoices/route.ts`).
 * Status publikacji/archiwum ZOSTAJĄ osobnymi kontrolkami w pasku tabeli (chipy +
 * checkbox) — nie idą przez popup nagłówka (mirror status/direction
 * w KSeF). Whitelist backendu → `HEADER_FILTER_FIELDS` w `apps/api/app/api/processes/route.ts`.
 *
 * Pominięte z MVP (TODO follow-up):
 *  - Duplikuj — wymagałby endpointu backendowego lub 3-call composition.
 *  - Publikuj-Cofnij — wymagałby endpointu `/unpublish` (PATCH /:id NIE
 *    przyjmuje `isPublished` w PatchProcessBodySchema).
 *  - Przywróć z kosza — brak endpointu `/restore`.
 *  - Paginacja UI — backend wspiera limit/offset (default 50), MVP bez UI.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, Layers, Plus, Send, Trash2, Workflow } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import {
  type ColumnDefinition,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
} from '@/components/configurable-table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { CreateProcessModal } from '@/components/process-builder/CreateProcessModal';
import { useToast } from '@/contexts/ToastContext';
import {
  deleteProcess,
  listProcesses,
  type ProcessListRow,
  publishProcess,
} from '@/lib/processes/api';

/**
 * Serializuje `ColumnHeaderFilters` (kolumnId → filtr, stan UI) do `?headerFilters=`
 * (JSON.stringify HeaderFilter[]) — mirror `KsefInvoices.tsx`/`Users.tsx`. KLUCZOWE:
 * `field` w wyjściowym obiekcie MUSI być `headerFilter.fieldSlug` kolumny, NIE
 * `columnId` z UI — kontrakt z whitelistą `HEADER_FILTER_FIELDS` w
 * `apps/api/app/api/processes/route.ts` operuje na backendowych slugach.
 * Czysta funkcja (generyczna po `T`, mirror KsefInvoices) — testowalna z
 * synthetycznym `ColumnDefinition` bez importowania `ProcessListRow`
 * (patrz `ProcessesListPage.header-filters.test.tsx`).
 */
export function serializeHeaderFiltersForRequest<T>(
  headerFilters: ColumnHeaderFilters,
  columns: ColumnDefinition<T>[],
): string | undefined {
  const merged: ColumnHeaderFilter[] = [];
  for (const [columnId, filter] of Object.entries(headerFilters)) {
    const col = columns.find((c) => c.id === columnId);
    const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
    if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
  }
  return merged.length > 0 ? JSON.stringify(merged) : undefined;
}

// Lokalny debounce hook — kopia ze wzorca Contractors.tsx / ContractorContacts.tsx.
// TODO follow-up: wyciągnąć do shared `apps/admin/src/hooks/useDebouncedValue.ts`.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

type PublishedFilter = 'all' | 'draft' | 'published';

function ProcessStatusBadge({ row }: { row: ProcessListRow }) {
  const { t } = useTranslation();
  if (row.gcrecord) {
    return (
      <span
        className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
        data-testid="process-status-archived"
      >
        {t('tenant.processes.list.status.archived')}
      </span>
    );
  }
  if (!row.isActive) {
    return (
      <span
        className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
        data-testid="process-status-inactive"
      >
        {t('tenant.processes.list.status.inactive')}
      </span>
    );
  }
  if (row.isPublished) {
    return (
      <span
        className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300"
        data-testid="process-status-published"
      >
        {t('tenant.processes.list.status.published')}
      </span>
    );
  }
  return (
    <span
      className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
      data-testid="process-status-draft"
    >
      {t('tenant.processes.list.status.draft')}
    </span>
  );
}

export function ProcessesListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [publishedFilter, setPublishedFilter] = useState<PublishedFilter>('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProcessListRow | null>(null);

  // Filtry per kolumna z popupu w nagłówku (Kreator Listy) — server-side, patrz
  // `serializedHeaderFilters` niżej. Status publikacji/archiwum zostają OSOBNYMI
  // kontrolkami nad tabelą (select + checkbox, niezmienione) — nie są foldowane
  // w wiersz nagłówka (mirror status/direction w `KsefInvoices.tsx`).
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});

  // Publish mutation — wywołane z akcji wiersza.
  const publishMutation = useMutation({
    mutationFn: (id: string) => publishProcess(id),
    onSuccess: (result, id) => {
      if (result.ok) {
        toast.show({
          variant: 'success',
          title: t('tenant.processes.list.toast.publishSuccess'),
        });
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
      } else if (result.error === 'GRAPH_VALIDATION_FAILED') {
        const issues = (result.issues ?? [])
          .map((i) => t(`tenant.processes.builder.publishCheck.issues.${i}`))
          .join(' • ');
        toast.show({
          variant: 'error',
          title: t('tenant.processes.list.toast.publishValidationFailed'),
          description: issues || undefined,
          duration: 8000,
        });
        // User powinien wejść do buildera i naprawić — oferta przejścia.
        navigate(`/processes/builder/${id}`);
      } else if (result.error === 'ALREADY_PUBLISHED') {
        toast.show({
          variant: 'warning',
          title: t('tenant.processes.list.toast.alreadyPublished'),
        });
      } else {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.list.toast.publishError'),
        });
      }
    },
  });

  // Soft-delete mutation.
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProcess(id),
    onSuccess: (result) => {
      if (result.ok) {
        toast.show({
          variant: 'success',
          title: t('tenant.processes.list.toast.deleteSuccess'),
        });
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
      } else if (result.error === 'PROCESS_HAS_ACTIVE_INSTANCES') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.list.toast.deleteHasActiveInstances'),
          description: result.count
            ? t('tenant.processes.list.toast.activeInstancesCount', { count: result.count })
            : undefined,
          duration: 8000,
        });
      } else {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.list.toast.deleteError'),
        });
      }
      setPendingDelete(null);
    },
  });

  function handleCreated(id: string) {
    // Po utworzeniu draft → od razu wejście do buildera.
    navigate(`/processes/builder/${id}`);
  }

  function handleEdit(row: ProcessListRow) {
    navigate(`/processes/builder/${row.id}`);
  }

  function handlePublish(row: ProcessListRow) {
    if (publishMutation.isPending) return;
    publishMutation.mutate(row.id);
  }

  function handleConfirmDelete() {
    if (!pendingDelete || deleteMutation.isPending) return;
    deleteMutation.mutate(pendingDelete.id);
  }

  // Kolumny listy (ConfigurableTable). `headerFilter` idzie WYŁĄCZNIE na kolumny
  // odpowiadające prostym, skalarnym kolumnom `processes` z whitelisty backendu
  // (`HEADER_FILTER_FIELDS` w `apps/api/app/api/processes/route.ts`) — kolumna
  // „Status" jest kompozytem (gcrecord + isActive + isPublished), bez 1:1 mapowania
  // na kolumnę, więc bez popupu filtra (mirror `status` w KsefInvoices.tsx).
  const columns = useMemo<ColumnDefinition<ProcessListRow>[]>(
    () => [
      {
        id: 'number',
        accessor: 'number',
        header: t('tenant.processes.list.columns.number'),
        defaultWidth: 70,
        sortable: false,
        headerFilter: { category: 'number', fieldSlug: 'number' },
        renderCell: (value) => (
          <span className="font-mono text-text-secondary">{String(value)}</span>
        ),
      },
      {
        id: 'name',
        accessor: 'name',
        header: t('tenant.processes.list.columns.name'),
        defaultWidth: 280,
        sortable: false,
        headerFilter: { category: 'text', fieldSlug: 'name' },
        renderCell: (_value, row) => {
          const isArchived = row.gcrecord !== null;
          return (
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={isArchived ? 'text-text-secondary' : 'text-text-primary'}
                data-testid={`processes-list-row-name-${row.id}`}
              >
                {row.name}
              </span>
              {row.isLinked && (
                <span
                  className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:ring-indigo-800"
                  title={t('tenant.processes.templates.linkedBadge')}
                  aria-label={t('tenant.processes.templates.linkedBadge')}
                  data-testid={`processes-list-linked-badge-${row.id}`}
                >
                  {t('tenant.processes.templates.linkedBadge')}
                </span>
              )}
              {!row.isLinked && row.sourceTemplateId && (
                <span
                  className="rounded-full bg-accent-50 px-1.5 py-0.5 text-xs font-medium text-accent-700 ring-1 ring-accent-200 dark:bg-accent-900/20 dark:text-accent-300 dark:ring-accent-800"
                  title={t('tenant.processes.templates.fromTemplateAriaLabel')}
                  aria-label={t('tenant.processes.templates.fromTemplateAriaLabel')}
                  data-testid={`processes-list-template-badge-${row.id}`}
                >
                  {t('tenant.processes.templates.fromTemplate')}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'target_module_code',
        accessor: 'targetModuleCode',
        header: t('tenant.processes.list.columns.module'),
        defaultWidth: 200,
        sortable: false,
        headerFilter: { category: 'text', fieldSlug: 'target_module_code' },
        renderCell: (value) => (
          <span className="font-mono text-xs text-text-secondary">{String(value)}</span>
        ),
      },
      {
        id: 'status',
        accessor: (row) =>
          row.gcrecord
            ? 'archived'
            : !row.isActive
              ? 'inactive'
              : row.isPublished
                ? 'published'
                : 'draft',
        header: t('tenant.processes.list.columns.status'),
        defaultWidth: 130,
        sortable: false,
        renderCell: (_value, row) => <ProcessStatusBadge row={row} />,
      },
      {
        id: 'version',
        accessor: 'version',
        header: t('tenant.processes.list.columns.version'),
        defaultWidth: 80,
        sortable: false,
        renderCell: (value) => <span className="text-text-secondary">v{String(value)}</span>,
      },
    ],
    [t],
  );

  const serializedHeaderFilters = useMemo(
    () => serializeHeaderFiltersForRequest(headerFilters, columns),
    [headerFilters, columns],
  );

  const query = useMemo(
    () => ({
      q: search.trim() || undefined,
      isPublished:
        publishedFilter === 'published' ? true : publishedFilter === 'draft' ? false : undefined,
      includeDeleted: includeArchived || undefined,
      headerFilters: serializedHeaderFilters,
    }),
    [search, publishedFilter, includeArchived, serializedHeaderFilters],
  );

  const listQuery = useQuery({
    queryKey: ['processes', query],
    queryFn: () => listProcesses(query),
    staleTime: 15_000,
  });

  const rows = listQuery.data?.data ?? [];
  const isFilteredView = Boolean(search.trim() || publishedFilter !== 'all' || includeArchived);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-6" data-testid="processes-list-page">
      {/* Nagłówek strony — ten sam wzorzec co listy kartotek (Kontrahenci): tytuł + opis po
          lewej, akcje po prawej (Figma: jeden przycisk Primary na widok, reszta Secondary). */}
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text-primary">
            {t('tenant.processes.list.title')}
          </h1>
          <p className="mt-1 text-sm text-text-tertiary">{t('tenant.processes.list.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            leftIcon={<Layers size={16} aria-hidden="true" />}
            onClick={() => navigate('/processes/templates')}
            data-testid="processes-list-from-template-btn"
          >
            {t('tenant.processes.templates.browseLinkLabel')}
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus size={16} aria-hidden="true" />}
            onClick={() => setCreateModalOpen(true)}
            data-testid="processes-list-new-btn"
          >
            {t('tenant.processes.list.newProcess')}
          </Button>
        </div>
      </header>

      {/* Error state — ConfigurableTable nie ma wbudowanego stanu błędu (tylko loading/empty),
          więc zostaje osobny blok jak przed migracją; tabelę pokazujemy tylko gdy nie ma błędu. */}
      {listQuery.isError && (
        <div
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300"
          data-testid="processes-list-error"
        >
          {t('tenant.processes.list.loadError')}
        </div>
      )}

      {/* Tabela — Kreator Listy (ConfigurableTable): filtry per kolumna server-side przez
          `headerFilters` (kontrolowane, patrz `serializedHeaderFilters` wyżej — filtr MUSI iść
          na backend, nie po pobranej stronie, bo listing nie ma paginacji UI). Wiersz filtrów
          renderuje kontrolki typowane (menu operatorów + edytor) dla kolumn z `headerFilter`;
          kolumny bez niego (status/wersja) NIE dostają pola — ekran nie kontroluje `columnFilters`,
          więc `FilterRowCell` daje pusty placeholder zamiast lokalnego filtra pobranej strony.
          `persistConfig={false}` — brak zapisu layoutu (mirror KsefInvoices.tsx). */}
      {!listQuery.isError && (
        <ConfigurableTable<ProcessListRow>
          tableId="processes"
          testIdPrefix="processes-list"
          fillHeight
          /* Górny pasek WEWNĄTRZ karty tabeli (Figma „Table toolbar"): po lewej pasek chipów
             statusu (jeden aktywny) + wyszukiwarka, po prawej „Pokaż archiwalne". Chipy = ten
             sam wzorzec co grupy kontrahentów (`data-chip-bar` → wygląd z skórki w index.css). */
          listToolbar={
            <div className="flex flex-wrap items-center gap-3">
              <div
                role="group"
                aria-label={t('tenant.processes.list.columns.status')}
                className="flex flex-wrap items-center gap-2"
                data-chip-bar="groups"
                data-testid="processes-list-filter-published"
              >
                {(
                  [
                    ['all', t('tenant.processes.list.filter.all')],
                    ['draft', t('tenant.processes.list.filter.drafts')],
                    ['published', t('tenant.processes.list.filter.published')],
                  ] as const
                ).map(([value, label]) => (
                  <Button
                    key={value}
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-pressed={publishedFilter === value}
                    onClick={() => setPublishedFilter(value)}
                    className={`rounded-md ${
                      publishedFilter === value
                        ? 'font-medium text-text-primary'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                    data-testid={`processes-list-filter-published-${value}`}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <Input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('tenant.processes.list.searchPlaceholder')}
                aria-label={t('tenant.processes.list.searchPlaceholder')}
                containerClassName="w-full sm:w-[280px]"
                data-testid="processes-list-search"
              />
              <div className="ml-auto">
                <Checkbox
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  label={t('tenant.processes.list.includeArchived')}
                  data-testid="processes-list-include-archived"
                />
              </div>
            </div>
          }
          columns={columns}
          data={rows}
          isLoading={listQuery.isLoading}
          getRowId={(row) => row.id}
          headerFilters={headerFilters}
          onHeaderFiltersChange={setHeaderFilters}
          persistConfig={false}
          showViewModeToggle={false}
          onRowClick={(row) => {
            if (row.gcrecord === null) handleEdit(row);
          }}
          renderActions={(row) => {
            const isArchived = row.gcrecord !== null;
            return (
              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                {!isArchived && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(row)}
                    aria-label={t('tenant.processes.list.actions.edit')}
                    title={t('tenant.processes.list.actions.edit')}
                    data-testid={`processes-list-edit-${row.id}`}
                  >
                    <Edit2 aria-hidden="true" />
                  </IconButton>
                )}
                {!isArchived && !row.isPublished && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePublish(row)}
                    disabled={publishMutation.isPending}
                    className="text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30"
                    aria-label={t('tenant.processes.list.actions.publish')}
                    title={t('tenant.processes.list.actions.publish')}
                    data-testid={`processes-list-publish-${row.id}`}
                  >
                    <Send aria-hidden="true" />
                  </IconButton>
                )}
                {!isArchived && (
                  <IconButton
                    variant="danger"
                    size="sm"
                    onClick={() => setPendingDelete(row)}
                    aria-label={t('tenant.processes.list.actions.archive')}
                    title={t('tenant.processes.list.actions.archive')}
                    data-testid={`processes-list-archive-${row.id}`}
                  >
                    <Trash2 aria-hidden="true" />
                  </IconButton>
                )}
              </div>
            );
          }}
          actionsColumnLabel={t('tenant.processes.list.columns.actions')}
          actionsColumnWidth={120}
          emptyIcon={Workflow}
          emptyTitle={
            isFilteredView
              ? t('tenant.processes.list.emptyFiltered')
              : t('tenant.processes.list.emptyAll')
          }
        />
      )}

      <CreateProcessModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleCreated}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.processes.list.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.processes.list.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('tenant.processes.list.actions.archive')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
